import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';

/**
 * Validates that a string represents a positive integer.
 * Returns { valid: true, value: number } or { valid: false, error: string }.
 */
const validatePositiveInteger = (value) => {
  if (value === '' || value === null || value === undefined) {
    return { valid: true, value: null }; // Empty means "inherit"
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return { valid: false, error: 'Must be a valid number' };
  }

  if (!Number.isFinite(num)) {
    return { valid: false, error: 'Value is out of range' };
  }

  if (!Number.isInteger(num)) {
    return { valid: false, error: 'Must be a whole number (no decimals)' };
  }

  if (num < 0) {
    return { valid: false, error: 'Must be a positive number' };
  }

  return { valid: true, value: num };
};

/**
 * ScanConfigCard — lets support users (OPS/ADMIN) configure streaming scan parameters
 * for a specific account.
 *
 * Props:
 *   apiKeyName     — name of the API key (for display)
 *   initialConfig  — { token_threshold, window_overlap, quick_timeout_s, max_accumulated_mb }
 *   role           — caller's AAD role: 'ADMIN' | 'OPS' | 'SUPPORT'
 *   onSave         — called with updated config object after the user saves
 *   isLoading      — external loading flag (e.g., during a POST)
 */
const ScanConfigCard = ({
  apiKeyName,
  initialConfig = {},
  role = 'SUPPORT',
  onSave,
  isLoading = false,
}) => {
  const tokens = useTokens();
  const [config, setConfig] = useState({
    token_threshold: initialConfig.token_threshold ?? '',
    window_overlap: initialConfig.window_overlap ?? '',
    quick_timeout_s: initialConfig.quick_timeout_s ?? '',
    max_accumulated_mb: initialConfig.max_accumulated_mb ?? '',
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, type: 'neutralSuccess', message: '' });

  // Sync local state when initialConfig changes
  useEffect(() => {
    setConfig({
      token_threshold: initialConfig.token_threshold ?? '',
      window_overlap: initialConfig.window_overlap ?? '',
      quick_timeout_s: initialConfig.quick_timeout_s ?? '',
      max_accumulated_mb: initialConfig.max_accumulated_mb ?? '',
    });
    setHasChanges(false);
    setErrors({});
  }, [initialConfig]);

  const readOnly = role === 'SUPPORT';

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    // Allow any input - validation happens on save
    setConfig((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSave = () => {
    // Validate all fields
    const fields = ['token_threshold', 'window_overlap', 'quick_timeout_s', 'max_accumulated_mb'];
    const newErrors = {};
    const payload = {};

    for (const field of fields) {
      const result = validatePositiveInteger(config[field]);
      if (!result.valid) {
        newErrors[field] = result.error;
      } else {
        payload[field] = result.value;
      }
    }

    // If any validation errors, show them and don't save
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Dispatch save - keep hasChanges true until success.
    // The useEffect on initialConfig clears hasChanges when the
    // successful response updates initialConfig.
    onSave?.(payload);
    setErrors({});
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const fieldDefs = [
    {
      key: 'token_threshold',
      label: 'Token threshold',
      helper: 'Trigger quick scan after N tokens (default: 500)',
    },
    {
      key: 'window_overlap',
      label: 'Window overlap',
      helper: 'Tokens to overlap between scans (default: 100)',
    },
    {
      key: 'quick_timeout_s',
      label: 'Quick timeout (s)',
      helper: 'Quick scan timeout in seconds (default: 5)',
    },
    {
      key: 'max_accumulated_mb',
      label: 'Max accumulated (MB)',
      helper: 'Max buffer size in MB (default: 10)',
    },
  ];

  return (
    <>
      <Card bdsType="staticSubtle" bdsBoxShadow="default" bdsPadding="spacious">
        <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
          <Typography variant="h4">
            Scan config{apiKeyName ? ` — ${apiKeyName}` : ''}
          </Typography>
          <Typography variant="captionSubtle" sx={{ maxWidth: 500 }}>
            Streaming inspection parameters for this account. Empty values inherit from
            Gateway or code defaults.
          </Typography>

          <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
            {fieldDefs.map((fd) => (
              <Tooltip
                key={fd.key}
                title={readOnly ? 'Requires OPS or ADMIN role to change.' : fd.helper}
              >
                <TextField
                  label={fd.label}
                  value={config[fd.key]}
                  onChange={handleChange(fd.key)}
                  disabled={readOnly || isLoading}
                  placeholder="(inherit)"
                  fullWidth
                  bdsSize="small"
                  error={Boolean(errors[fd.key])}
                  helperText={errors[fd.key] || fd.helper}
                />
              </Tooltip>
            ))}
          </Stack>

          {hasChanges && !readOnly && (
            <Stack
              direction="row"
              sx={{ gap: tokens.aliasesSpaceComponentDefault, justifyContent: 'flex-end' }}
            >
              <Button
                bdsType="interactiveEmphasis"
                onClick={handleSave}
                isLoading={isLoading}
              >
                Save
              </Button>
            </Stack>
          )}

          {readOnly && (
            <Typography variant="captionSubtle">
              Requires OPS or ADMIN role to change.
            </Typography>
          )}
        </Stack>
      </Card>

      {/* Feedback snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          bdsType={snackbar.type}
          bdsBoxShadowType="floating"
          onClose={handleSnackbarClose}
          sx={{ padding: tokens.aliasesSpaceContainerSpacious }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ScanConfigCard;
